// frontend/src/pages/TeacherVideoUpload.jsx

import React, { useEffect, useState, useCallback } from "react"; // useCallback を追加
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import { Link } from "react-router-dom";
import { API_URL } from "../config";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  List,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Chip,
  useTheme,
  Alert // Alert をインポート
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import VideoDurationEditor from '../components/VideoDurationEditor';
import Autocomplete from "@mui/material/Autocomplete";

// (CONTENT_COLORS, CONTENT_TYPES, formatVideoDurationForDisplay は変更なし)
const CONTENT_COLORS = {
  "Python基礎": "#76B900",
  "最新AI調査": "#1E88E5",
  "その他":     "#757575",
};

const CONTENT_TYPES = [
  { value: "Python基礎", label: "Python基礎" },
  { value: "最新AI調査", label: "最新AI調査" },
  { value: "その他", label: "その他" },
];

const formatVideoDurationForDisplay = (secs) => {
  if (secs === null || secs === undefined || isNaN(secs) || secs < 0) return "未設定";
  if (secs === 0) return "0秒";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  let result = "";
  if (h > 0) result += `${h}時間 `;
  if (m > 0 || (h > 0 && s >=0 )) result += `${m}分 `;
  result += `${s}秒`;
  return result.trim() || "0秒";
};


const TeacherVideoUpload = () => {
  const theme = useTheme();
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [contentType, setContentType] = useState(CONTENT_TYPES[0]?.value || "");
  const [contentTypeOptions, setContentTypeOptions] = useState(() => CONTENT_TYPES.map(c => c.value));

  // --- 科目関連の state ---
  const [subjects, setSubjects] = useState([]); // 担当科目リスト
  const [selectedSubjectId, setSelectedSubjectId] = useState(""); // 選択された科目ID
  const [subjectsLoading, setSubjectsLoading] = useState(true); // 科目リストのローディング

  const [manualDuration, setManualDuration] = useState("");
  const [message, setMessage] = useState("");
  const [uploadStatus, setUploadStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'

  const [videos, setVideos] = useState([]);
  const [editVideoId, setEditVideoId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editContentType, setEditContentType] = useState("");
  const [editSubjectId, setEditSubjectId] = useState(""); // ★ 編集時の科目ID用

  const [listLoading, setListLoading] = useState(true); // 動画リストのローディング

  const textColor = theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.primary;
  const paperBgColor = theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[50];
  const inputLabelColor = theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.text.secondary;
  const helperTextColor = theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.text.secondary;
  const borderColor = theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[400];

  const fetchTeacherSubjects = useCallback(async () => {
    setSubjectsLoading(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        // setMessage("科目リスト取得のためにはログインが必要です。"); // 必要に応じてメッセージ表示
        setSubjects([]);
        return;
      }
      const res = await axios.get(`${API_URL}/subjects/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const fetchedSubjects = res.data || [];
      setSubjects(fetchedSubjects);
      if (fetchedSubjects.length === 1) {
        setSelectedSubjectId(fetchedSubjects[0].id); // 担当科目が1つなら自動選択
      } else {
        setSelectedSubjectId(""); // 複数または0件なら未選択状態に
      }
    } catch (err) {
      console.error("Error fetching teacher subjects:", err);
      setMessage("担当科目の取得に失敗しました。"); // アップロードフォームにも影響するのでエラー表示
      setSubjects([]);
    } finally {
      setSubjectsLoading(false);
    }
  }, []);


  const fetchVideos = useCallback(async () => {
    setListLoading(true); // 動画リスト専用のローディングstate
    const token = localStorage.getItem("token");
    if (!token) {
      setMessage("教員としてログインしてください。");
      setListLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/videos/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const formattedVideos = res.data.map(v => ({
        ...v,
        duration: (v.duration === null || v.duration === undefined) ? null : (parseFloat(v.duration) || null)
      }));
      setVideos(formattedVideos);
      // 既存動画のコンテンツ種別をオプションに取り込む
      const existingTypes = formattedVideos.map(v => v.content_type).filter(Boolean);
      const defaults = CONTENT_TYPES.map(c => c.value);
      const unique = Array.from(new Set([...defaults, ...existingTypes]));
      setContentTypeOptions(unique);
    } catch (err) {
      // setMessage は科目取得やアップロードと競合する可能性があるので、リスト取得失敗専用の表示を検討
      console.error("動画リストの取得に失敗しました:", err.response?.data?.detail || err.message);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeacherSubjects();
    fetchVideos();
  }, [fetchTeacherSubjects, fetchVideos]);

  const resetForm = useCallback(() => {
    setTitle("");
    setUrl("");
    setContentType(contentTypeOptions[0] || CONTENT_TYPES[0]?.value || "");
    setManualDuration("");
    // 科目選択のリセット (担当科目が1つなら再度自動選択)
    if (subjects.length === 1) {
      setSelectedSubjectId(subjects[0].id);
    } else {
      setSelectedSubjectId("");
    }
  }, [subjects, contentTypeOptions]);


  const handleUpload = async (e) => {
    e.preventDefault();
    setMessage("");
    setUploadStatus('loading');

    if (!selectedSubjectId) {
      setMessage("動画を登録する科目を選択してください。");
      setUploadStatus('error');
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const payload = {
        title,
        url,
        content_type: contentType,
        subject_id: selectedSubjectId, // ★ 選択された科目IDを追加
      };
      const durationSec = parseInt(manualDuration, 10);
      if (!isNaN(durationSec) && durationSec >= 0) {
        payload.duration = durationSec;
      } else if (manualDuration === "") { // 空文字の場合は duration を送信しない (API側で自動取得試行)
        delete payload.duration;
      }


      const res = await axios.post(
        `${API_URL}/videos/`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchVideos(); // 動画リストを再取得して更新
      resetForm();
      setMessage(`動画「${res.data.title}」を科目「${res.data.subject.name}」に登録しました。 (ID=${res.data.id})`);
      setUploadStatus('success');
      if (res.data?.content_type && !contentTypeOptions.includes(res.data.content_type)) {
        setContentTypeOptions(prev => [...prev, res.data.content_type]);
      }
    } catch (err) {
      console.error("Upload error:", err.response);
      let errorMessage = "動画のアップロードに失敗しました。";
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail) && err.response.data.detail[0]?.msg) {
          errorMessage = `入力エラー: ${err.response.data.detail[0].loc.join('.')} - ${err.response.data.detail[0].msg}`;
        } else {
          errorMessage = "サーバー側でエラーが発生しました。入力内容を確認してください。";
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setMessage(errorMessage);
      setUploadStatus('error');
    }
  };

  const handleEditClick = (video) => {
    setEditVideoId(video.id);
    setEditTitle(video.title);
    setEditContentType(video.content_type);
    setEditSubjectId(video.subject_id || video.subject?.id || ""); // ★ 編集時の科目をセット
    setMessage("");
    setUploadStatus('idle');
  };

  const handleCancelEdit = () => {
    setEditVideoId(null);
    setEditTitle("");
    setEditContentType("");
    setEditSubjectId(""); // ★ キャンセル時リセット
    setMessage("");
    setUploadStatus('idle');
  };

  const handleSaveTitle = async (videoId) => {
    setMessage("");
    setUploadStatus('loading');

    if (!editSubjectId && editVideoId === videoId) { // 編集中で科目IDが未選択の場合
        setMessage("科目が選択されていません。");
        setUploadStatus('error');
        return;
    }

    try {
      const token = localStorage.getItem("token");
      const payload = {
        title: editTitle,
        content_type: editContentType,
        subject_id: editSubjectId, // ★ 編集時も科目IDを送信
      };
      const res = await axios.patch(
        `${API_URL}/videos/${videoId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // fetchVideos を呼ぶと編集状態がリセットされてしまうので、ローカルで更新
      setVideos((prevVideos) =>
        prevVideos.map((v) =>
          v.id === videoId
            ? { // APIレスポンスに基づいて更新
                ...v,
                title: res.data.title,
                content_type: res.data.content_type,
                subject: res.data.subject, // subjectオブジェクト全体で更新
                subject_id: res.data.subject_id,
              }
            : v
        )
      );
      handleCancelEdit(); // 編集モードを終了
      setMessage(`動画ID ${videoId} を更新しました。`);
      setUploadStatus('success');
      if (res.data?.content_type && !contentTypeOptions.includes(res.data.content_type)) {
        setContentTypeOptions(prev => [...prev, res.data.content_type]);
      }
    } catch (err) {
      let errorMessage = `動画ID ${videoId} の更新に失敗しました。`;
       if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail) && err.response.data.detail[0]?.msg) {
          errorMessage = `入力エラー: ${err.response.data.detail[0].loc.join('.')} - ${err.response.data.detail[0].msg}`;
        }
      }
      setMessage(errorMessage);
      setUploadStatus('error');
    }
  };

  // (handleDelete, handleDragEnd, handleDurationUpdatedOnList は大きな変更なし、メッセージ部分を調整する可能性あり)
  const handleDelete = async (videoId) => {
    if (!window.confirm(`動画ID: ${videoId} を本当に削除しますか？関連する視聴ログも全て削除されます。`)) return;
    setMessage("");
    setUploadStatus('loading');
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVideos((prev) => prev.filter((v) => v.id !== videoId));
      setMessage(`動画ID ${videoId} を削除しました。`);
      setUploadStatus('success');
    } catch (err) {
      setMessage(err.response?.data?.detail || err.message || `動画ID ${videoId} の削除に失敗しました。`);
      setUploadStatus('error');
    }
  };

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    const items = Array.from(videos);
    const [reorderedItem] = items.splice(source.index, 1);
    items.splice(destination.index, 0, reorderedItem);

    setVideos(items);
    setMessage(''); // 並び替え中のメッセージはクリア
    setUploadStatus('loading');

    const token = localStorage.getItem("token");
    const videoIdsInNewOrder = items.map((v) => v.id);

    axios.put(`${API_URL}/videos/order`,
        { video_ids: videoIdsInNewOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => {
        setMessage("動画の並び順を更新しました。");
        setUploadStatus('success');
      })
      .catch((err) => {
        console.error("Error reordering videos:", err.response?.data?.detail || err.message);
        setMessage("動画の並び順の更新に失敗しました。ページを再読み込みして最新の状態を確認してください。");
        setUploadStatus('error');
        fetchVideos(); // 失敗時はサーバーの状態に戻す
      });
  };

 const handleDurationUpdatedOnList = (updatedVideoId, newDuration) => {
    setVideos(prevVideos =>
      prevVideos.map(video =>
        video.id === updatedVideoId ? { ...video, duration: newDuration } : video
      )
    );
    // これは VideoDurationEditor からの直接の成功コールバックなので、メッセージとステータスをセット
    setMessage(`動画ID ${updatedVideoId} の長さが ${formatVideoDurationForDisplay(newDuration)} に更新されました。`);
    setUploadStatus('success');
  };


  if (subjectsLoading || listLoading && videos.length === 0) { // 科目リストと動画リストの初回読み込み
    return <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, backgroundColor: paperBgColor, color: textColor }}>
        <Typography variant="h5" gutterBottom component="h1" sx={{ fontWeight: 'bold', color: textColor }}>
          動画のアップロードと管理
        </Typography>
        <form onSubmit={handleUpload}>
          <TextField label="動画タイトル" fullWidth margin="normal" value={title} onChange={(e) => setTitle(e.target.value)} required
            InputLabelProps={{style: {color: inputLabelColor}}} InputProps={{style: {color: textColor}}} sx={{"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: borderColor }, "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.light } }}/>
          <TextField label="動画URL (YouTube推奨)" fullWidth margin="normal" value={url} onChange={(e) => setUrl(e.target.value)} required
            InputLabelProps={{style: {color: inputLabelColor}}} InputProps={{style: {color: textColor}}} sx={{"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: borderColor }, "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.light } }}/>

          {/* --- 科目選択ドロップダウン --- */}
          {subjectsLoading ? (
            <CircularProgress size={24} sx={{mt:2, mb:1}} />
          ) : subjects.length === 0 ? (
            <Alert severity="warning" sx={{mt:2, mb:1}}>担当している科目がありません。まず科目を登録または割り当ててください。</Alert>
          ) : (
            <FormControl fullWidth margin="normal" required>
              <InputLabel id="subject-select-label" sx={{color: inputLabelColor}}>科目</InputLabel>
              <Select
                labelId="subject-select-label"
                value={selectedSubjectId}
                label="科目"
                onChange={(e) => setSelectedSubjectId(e.target.value)}
                sx={{color: textColor, "& .MuiOutlinedInput-notchedOutline": { borderColor: borderColor }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.light }, "& .MuiSvgIcon-root": {color: inputLabelColor} }}
                MenuProps={{ PaperProps: { sx: { backgroundColor: paperBgColor, color: textColor } }}}
              >
                {subjects.length > 1 && <MenuItem value=""><em>科目を選択してください</em></MenuItem> }
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* --- ここまで科目選択 --- */}

          <Autocomplete
            freeSolo
            options={contentTypeOptions}
            value={contentType}
            onChange={(e, newValue) => {
              if (typeof newValue === 'string') {
                setContentType(newValue);
                if (newValue && !contentTypeOptions.includes(newValue)) {
                  setContentTypeOptions(prev => [...prev, newValue]);
                }
              } else if (newValue) {
                setContentType(newValue);
              }
            }}
            onInputChange={(e, newInput) => {
              if (typeof newInput === 'string') setContentType(newInput);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="コンテンツ種別 (動画固有)"
                margin="normal"
                InputLabelProps={{style: {color: inputLabelColor}}}
                InputProps={{ ...params.InputProps, style: {color: textColor} }}
              />
            )}
          />
          <TextField
            label="動画の長さ (秒) - (任意、YouTube以外や自動取得失敗時)"
            type="number" fullWidth margin="normal" value={manualDuration}
            onChange={(e) => setManualDuration(e.target.value)}
            InputProps={{ inputProps: { min: 0 }, style: {color: textColor} }}
            InputLabelProps={{style: {color: inputLabelColor}}}
            helperText="YouTube動画の場合は通常自動取得されます。手動で秒数を入力することも可能です。"
            FormHelperTextProps={{style: {color: helperTextColor}}}
            sx={{"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: borderColor }, "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.primary.light } }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            disabled={subjectsLoading || subjects.length === 0 || uploadStatus === 'loading'} // 科目ロード中・科目なし・アップロード処理中は無効
          >
            {uploadStatus === 'loading' ? <CircularProgress size={24} color="info" /> : '動画を登録'}
          </Button>
        </form>
        {message && (
          <Alert
            severity={uploadStatus === 'success' ? 'success' : uploadStatus === 'error' ? 'error' : 'info'}
            sx={{ mt: 2 }}
            onClose={uploadStatus !== 'loading' ? () => { setMessage(''); setUploadStatus('idle'); } : undefined}
          >
            {message}
          </Alert>
        )}
      </Paper>

      {/* --- 登録済み動画リスト --- */}
      <Paper sx={{ p: { xs: 2, md: 3 }, backgroundColor: paperBgColor, color: textColor }}>
        <Typography variant="h6" gutterBottom sx={{color: textColor}}>
          登録済み動画 (ドラッグ＆ドロップで並び替え可能)
        </Typography>
        {listLoading && videos.length === 0 && <Box sx={{display: 'flex', justifyContent: 'center', py:3}}><CircularProgress /></Box>}
        {!listLoading && videos.length === 0 && (
            <Typography sx={{ textAlign: 'center', py: 3, color: textColor }}>登録されている動画はありません。</Typography>
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="videoList">
            {(provided) => (
              <List {...provided.droppableProps} ref={provided.innerRef} sx={{ p: 0 }}>
                {videos.map((video, index) => (
                  <Draggable key={video.id} draggableId={String(video.id)} index={index}>
                    {(providedDrag) => (
                      <Paper
                        elevation={2}
                        ref={providedDrag.innerRef}
                        {...providedDrag.draggableProps}
                        {...providedDrag.dragHandleProps}
                        sx={{
                          mb: 1.5, p: 2,
                          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
                          color: textColor,
                          borderRadius:1
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { sm: 'center' },
                            gap: 1.5,
                          }}
                        >
                          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Tooltip title={`科目: ${video.subject?.name || '未割り当て'}`}>
                                <Chip
                                  label={video.subject?.name || `ID:${video.subject_id || '?'}`}
                                  size="small"
                                  color="info" // テーマカラーの info を使う
                                  variant="outlined"
                                  sx={{textTransform: 'none'}}
                                />
                              </Tooltip>
                              <Chip
                                label={video.content_type}
                                size="small"
                                sx={{
                                  textTransform: 'none',
                                  backgroundColor: CONTENT_COLORS[video.content_type] || CONTENT_COLORS["その他"],
                                  color: '#fff'
                                }}
                              />
                            </Box>

                            <Typography
                              variant={editVideoId === video.id ? "body1" : "subtitle1"}
                              component="div"
                              title={video.title}
                              sx={{
                                color: textColor,
                                fontWeight: editVideoId === video.id ? 'normal' : 'bold',
                                wordBreak: 'break-word',
                              }}
                            >
                              {index + 1}. {editVideoId === video.id ? (
                                <TextField
                                  size="small"
                                  value={editTitle}
                                  onChange={(e) => setEditTitle(e.target.value)}
                                  fullWidth
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true, style: {color: inputLabelColor} }}
                                  InputProps={{ style: {color: textColor, fontSize: '0.9rem'} }}
                                  sx={{mt: 0.5}}
                                />
                              ) : (
                                <span style={{ display: 'inline-block', width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                  {video.title}
                                </span>
                              )}
                            </Typography>
                            <Typography variant="caption" sx={{color: helperTextColor}}>
                              (動画ID: {video.id}, 動画長: {formatVideoDurationForDisplay(video.duration)})
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              flexShrink: 0,
                              flexWrap: 'nowrap',
                              mt: { xs: 1.5, sm: 0 }
                            }}
                          >
                            <Button
                              variant="outlined"
                              size="small"
                              color="primary"
                              component={Link}
                              to={`/teacher/video/${video.id}`}
                              sx={{whiteSpace: 'nowrap'}}
                            >
                              視聴状況
                            </Button>
                            {editVideoId === video.id ? (
                              <>
                                <FormControl size="small" sx={{ minWidth: 150, mx: 0.5 }}>
                                  <InputLabel shrink sx={{color: inputLabelColor}}>科目</InputLabel>
                                  <Select
                                    value={editSubjectId}
                                    label="科目"
                                    onChange={(e) => setEditSubjectId(e.target.value)}
                                    sx={{color: textColor, "& .MuiOutlinedInput-notchedOutline": { borderColor: borderColor }, "& .MuiSvgIcon-root": {color: inputLabelColor}, fontSize: '0.875rem' }}
                                    MenuProps={{ PaperProps: { sx: { backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.background.paper , color: textColor } }}}
                                    disabled={subjectsLoading}
                                  >
                                    {subjects.map((opt) => (<MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>))}
                                  </Select>
                                </FormControl>
                                <Autocomplete
                                  freeSolo
                                  size="small"
                                  sx={{ minWidth: 160, mx: 0.5 }}
                                  options={contentTypeOptions}
                                  value={editContentType}
                                  onChange={(e, newVal) => {
                                    if (typeof newVal === 'string') {
                                      setEditContentType(newVal);
                                      if (newVal && !contentTypeOptions.includes(newVal)) {
                                        setContentTypeOptions(prev => [...prev, newVal]);
                                      }
                                    } else if (newVal) {
                                      setEditContentType(newVal);
                                    }
                                  }}
                                  onInputChange={(e, inputVal) => {
                                    if (typeof inputVal === 'string') setEditContentType(inputVal);
                                  }}
                                  renderInput={(params) => (
                                    <TextField {...params} label="種別" InputLabelProps={{ shrink: true, style: {color: inputLabelColor} }} />
                                  )}
                                />
                                <Button variant="contained" color="success" size="small" onClick={() => handleSaveTitle(video.id)} sx={{whiteSpace: 'nowrap'}} disabled={uploadStatus === 'loading'}>
                                  {uploadStatus === 'loading' && editVideoId === video.id ? <CircularProgress size={20} color="inherit"/> : "保存"}
                                </Button>
                                <Button variant="outlined" size="small" onClick={handleCancelEdit} sx={{color: textColor, borderColor: helperTextColor, whiteSpace: 'nowrap'}}>取消</Button>
                              </>
                            ) : (
                              <>
                                <Button variant="outlined" size="small" onClick={() => handleEditClick(video)} sx={{color: textColor, borderColor: helperTextColor, whiteSpace: 'nowrap', ml: 0.5}}>編集</Button>
                                <Button variant="outlined" size="small" color="error" onClick={() => handleDelete(video.id)} sx={{whiteSpace: 'nowrap', ml: 0.5}} disabled={uploadStatus === 'loading'}>削除</Button>
                              </>
                            )}
                          </Box>
                        </Box>
                        {(video.duration === null || video.duration === undefined || video.duration <= 0 || editVideoId === video.id) && (
                           <Box sx={{mt: 1.5, pt: 1.5, borderTop: `1px solid ${theme.palette.divider}`}}>
                             <VideoDurationEditor
                               videoId={video.id}
                               currentDuration={video.duration}
                               onDurationUpdated={handleDurationUpdatedOnList}
                             />
                           </Box>
                        )}
                      </Paper>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </Paper>
    </Container>
  );
};

export default TeacherVideoUpload;